import React, { useState } from "react";
import { cls } from "cls";
import CategoryFilterItem from "./CategoryFilterItem";
import { SectionGroup, SectionGroupItem } from "./Filters";
import StandardIcon from "~/common/Icons/Standard";

export interface GroupProps {
  readonly group: SectionGroup;
  readonly toggleCollapsed: (group: SectionGroup) => void;
  readonly prefetch?: boolean;
}

const DisplayCategoryItem = ({
  item,
  prefetch,
}: {
  readonly item: SectionGroupItem;
  readonly prefetch?: boolean;
}) => <CategoryFilterItem groupItem={item} prefetch={prefetch} />;

const CategoryFilterGroup = ({
  group,
  toggleCollapsed,
  prefetch,
}: GroupProps) => {
  const [showMoreLink, setShowMoreLink] = useState(true);

  const isVisibleItem = (item: SectionGroupItem) =>
    item.percentageOfHits && item.percentageOfHits > 2;

  const visibleItems = group.groupItems.filter(
    (item, index) => index === 0 || isVisibleItem(item),
  );
  const hiddenItems = group.groupItems.filter(
    (item, index) => index !== 0 && !isVisibleItem(item),
  );
  const groupHasSelectedCategory =
    group.groupItems.filter((item) => item.isSelected).length > 0;

  const handleMoreLinkClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setShowMoreLink(!showMoreLink);
  };

  return (
    <React.Fragment
      key={`categoryFilter-${group.groupName.replace(/[^A-Za-z0-9]+/gi, "-")}`}
    >
      <button
        type="button"
        className={cls(
          "filter-list__heading",
          "leading-none",
          "text-sm-1/2",
          "text-start",
          "w-full",
          "lt:pl-2",
          "focus:outline-offset-[-3px]",
          "mb-0.5",
          group.collapsed ? "collapsed" : "",
        )}
        aria-expanded={!group.collapsed}
        onClick={() => toggleCollapsed(group)}
      >
        <span className="filter-list__content font-bold ">
          {group.groupName}
        </span>
        <i className="icon-down-micro" />
      </button>
      <div
        className={`filter-list__itemlist${
          group.collapsed ? " collapse-animation hidden" : ""
        }`}
      >
        {groupHasSelectedCategory
          ? visibleItems
              .filter((groupItem) => groupItem.isSelected)
              .map((groupItem) => (
                <DisplayCategoryItem
                  key={`categoryFilterItem-${groupItem.label}`}
                  item={groupItem}
                  prefetch={prefetch}
                />
              ))
          : visibleItems.map((groupItem) => (
              <DisplayCategoryItem
                key={`categoryFilterItem-${groupItem.label}`}
                item={groupItem}
                prefetch={prefetch}
              />
            ))}
        {hiddenItems.length > 0 && !groupHasSelectedCategory && (
          <div className="filter-list__itemlist in">
            <div
              className={`collapse-animation${
                showMoreLink ? " visible lt:hidden" : " show"
              }`}
            >
              {hiddenItems.map((groupItem) => (
                <DisplayCategoryItem
                  key={`categoryFilterItem-${groupItem.label}`}
                  item={groupItem}
                  prefetch={prefetch}
                />
              ))}
            </div>
            <button
              type="button"
              className="moreFewer relative z-20"
              onClick={handleMoreLinkClick}
              aria-expanded={!showMoreLink}
            >
              <StandardIcon
                name="chevron-open-grey"
                style={{ left: 12, top: "50%" }}
                className={cls(
                  "h-2",
                  "absolute",
                  "w-2",
                  "transform",
                  "-translate-y-1/2",
                  "transition-all",
                  "ease-in",
                  "duration-300",
                  !showMoreLink && "rotate-180",
                )}
              />
              {` ${showMoreLink ? "More" : "Fewer"}`}
              <span className="sr-only">{group.groupName} options</span>
            </button>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default CategoryFilterGroup;
